import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";
import { ArrowUpRight, MapPin, Mic, Heart, MessageCircle } from "lucide-react";
import SobrietyCounter from "@/components/SobrietyCounter";
import QuoteOfDay from "@/components/QuoteOfDay";
import EventsSection from "@/components/EventsSection";
import SocialLinks from "@/components/SocialLinks";

const HERO_IMG = "https://images.unsplash.com/photo-1777245397587-a4bb478a739f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxjb3VwbGUlMjBoaWtpbmclMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzgxNTk1MjY3fDA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG = "https://images.unsplash.com/photo-1628531832865-989e137150ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxjb3VwbGUlMjBoaWtpbmclMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzgxNTk1MjY3fDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [destinations, setDestinations] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [about, setAbout] = useState(null);

  useEffect(() => {
    api.get("/destinations").then((r) => setDestinations(r.data)).catch(() => {});
    api.get("/podcast").then((r) => setPodcasts(r.data)).catch(() => {});
    api.get("/about").then((r) => setAbout(r.data)).catch(() => {});
  }, []);

  const current = destinations.find((d) => d.status === "current");
  const upcoming = destinations.filter((d) => d.status === "upcoming").slice(0, 3);
  const latestPodcast = podcasts[0];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative w-full overflow-hidden bg-black">
        {/* Deep atmospheric gradient — foundation of composition */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 72% 45%, rgba(224, 122, 95, 0.22) 0%, rgba(30, 63, 51, 0.14) 30%, transparent 60%), linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 100%)",
          }}
        />
        {/* Warm vignette on the right side to anchor logo */}
        <div
          className="absolute right-[-10%] top-[10%] w-[70%] h-[80%] pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at center, rgba(224, 122, 95, 0.15) 0%, transparent 55%)",
            filter: "blur(24px)",
          }}
        />
        <div className="absolute inset-0 grain opacity-25 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 items-center gap-10 md:gap-8 py-16 md:py-24">
          {/* Text side */}
          <div className="md:col-span-5 fade-up order-2 md:order-1">
            <div className="overline text-[var(--sand)] mb-5">A nationwide recovery movement</div>
            <h1 className="font-serif-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight">
              We didn&apos;t just survive addiction.<br />
              <em className="font-light italic text-[var(--sand)]">We escaped it.</em>
            </h1>
            <div className="space-y-4 text-white/85 text-base md:text-[17px] mt-8 max-w-xl leading-relaxed">
              <p>After more than <strong className="text-white font-normal">50 years trapped in addiction</strong>, we are living proof that recovery is possible.</p>
              <p>Now we&apos;re hitting the road with one mission: to travel to <strong className="text-[var(--sand)] font-normal">every state in America</strong>, unite recovery communities, and bring hope to those still suffering.</p>
              <p>This journey is bigger than us. It&apos;s about showing people that addiction doesn&apos;t have to be the end of their story. Together we&apos;ll host events, connect with local recovery communities, and remind people that <em className="text-[var(--sand)]">a better life is waiting</em>.</p>
              <p className="text-white font-medium tracking-tight">Join us. Follow our journey. Share our mission.</p>
              <p>Together, we can spread hope and fight the addiction epidemic across America — <span className="text-[var(--sand)] italic">one state, one city, and one life at a time.</span></p>
            </div>
          </div>

          {/* Photo side — cinematic composition of the rig at sunset */}
          <div className="md:col-span-7 order-1 md:order-2">
            <div className="relative w-full max-w-[720px] mx-auto md:ml-auto md:mr-0">
              {/* Sunset-warm ambient halo bleeding out from the photo */}
              <div
                className="absolute -inset-10 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse at 50% 30%, rgba(255,166,110,0.22) 0%, rgba(224,122,95,0.14) 30%, rgba(30,63,51,0.08) 55%, transparent 78%)",
                  filter: "blur(50px)",
                }}
              />
              {/* Cool blue undertone from the mountains and trailer graphic */}
              <div
                className="absolute -inset-6 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle at 20% 70%, rgba(56,80,120,0.20) 0%, transparent 55%)",
                  filter: "blur(36px)",
                }}
              />
              {/* Framed photo with warm-toned inner ring for editorial feel */}
              <div
                className="hero-logo-float relative rounded-2xl overflow-hidden ring-1 ring-white/12"
                style={{
                  boxShadow:
                    "0 40px 80px -20px rgba(0,0,0,0.55), 0 20px 40px -10px rgba(224,122,95,0.20), 0 0 0 1px rgba(247,241,231,0.05)",
                }}
              >
                <img
                  src="/hero-rv.webp"
                  alt="The 50 States Sober RV heading into a desert sunset"
                  className="relative w-full h-auto object-cover block"
                  style={{ filter: "saturate(1.02) contrast(1.02)" }}
                  loading="eager"
                  fetchPriority="high"
                />
                {/* Subtle inner vignette to seat the image */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    boxShadow: "inset 0 0 120px 20px rgba(0,0,0,0.28)",
                  }}
                />
              </div>
              {/* Ambient grain overlay across the photo area */}
              <div
                className="absolute -inset-4 grain opacity-30 pointer-events-none mix-blend-overlay rounded-3xl"
                style={{
                  maskImage: "radial-gradient(ellipse at center, black 40%, transparent 85%)",
                  WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 85%)",
                }}
              />
            </div>
          </div>
        </div>

        {/* Centered bottom CTA — "See where we are now" */}
        <div className="relative pb-10 md:pb-14 flex justify-center">
          <Link
            to="/travels"
            data-testid="hero-cta-travels"
            className="group inline-flex items-center gap-2 bg-[var(--terracotta)] text-[var(--ink)] px-8 py-4 rounded-full text-sm font-medium tracking-tight hover:bg-[var(--terracotta-dark)] transition-all hover:-translate-y-0.5 shadow-[0_18px_50px_-12px_rgba(224,122,95,0.6),0_8px_20px_-4px_rgba(0,0,0,0.4)]"
          >
            See where we are now
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </section>

      {/* QUOTE OF THE DAY */}
      <QuoteOfDay />

      {/* SOBRIETY COUNTER WALL */}
      <SobrietyCounter />

      {/* COUPLE PHOTO — cinematic block right before the intro */}
      <CouplePhotoSection about={about} />

      {/* MEET RICK & STEPHANIE */}
      <MeetSection about={about} />

      {/* EVENTS */}
      <EventsSection />

      {/* CURRENT LOCATION */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-5">
            <div className="overline mb-4">Right now</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mb-6">
              {current ? <>We're in <em className="italic text-[var(--terracotta)]">{current.location}</em></> : "We're on the road"}
            </h2>
            <p className="text-[var(--ink-soft)] leading-relaxed text-lg">
              {current?.description || "Our latest stop will appear here. Check the travels page for the full journey and the upcoming cities where we'd love to meet anyone walking the road of recovery."}
            </p>
            {current && (
              <div className="mt-6 flex items-center gap-2 text-sm text-[var(--ink-soft)]">
                <MapPin className="w-4 h-4 text-[var(--terracotta)]" strokeWidth={1.5} />
                <span className="font-mono-acc">{current.country || current.location}</span>
              </div>
            )}
            <Link to="/travels" data-testid="home-view-travels"
              className="inline-flex items-center gap-2 mt-8 text-sm border-b border-[var(--ink)] pb-1 hover:text-[var(--terracotta)] hover:border-[var(--terracotta)] transition-colors">
              View the full timeline <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="md:col-span-7">
            <div className="relative">
              <img src={current?.image_url || ABOUT_IMG} alt="Current destination"
                className="w-full h-[480px] object-cover rounded-sm" />
              <div className="absolute -bottom-6 -left-6 bg-[var(--bg)] border border-[var(--line)] p-6 hidden md:block">
                <div className="font-mono-acc text-xs text-[var(--ink-soft)]">CURRENT STOP</div>
                <div className="font-serif-display text-2xl mt-1">{current?.location || "Coming soon"}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* UPCOMING + PODCAST + DONATE bento */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28">
        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-7 p-10 md:p-12 rounded-sm" style={{ background: "var(--bg-alt)" }}>
            <div className="flex items-center gap-2 mb-6">
              <MapPin className="w-4 h-4 text-[var(--terracotta)]" strokeWidth={1.5} />
              <div className="overline">Next stops</div>
            </div>
            <h3 className="font-serif-display text-3xl mb-8">Where we're heading</h3>
            {upcoming.length === 0 ? (
              <p className="text-[var(--ink-soft)]">No upcoming destinations posted yet.</p>
            ) : (
              <ul className="space-y-5">
                {upcoming.map((d) => (
                  <li key={d.id} data-testid={`upcoming-${d.id}`} className="flex items-start justify-between gap-6 pb-5 border-b border-[var(--line)] last:border-b-0">
                    <div>
                      <div className="font-serif-display text-2xl">{d.location}</div>
                      <div className="text-sm text-[var(--ink-soft)] mt-1">{d.title}</div>
                    </div>
                    <div className="font-mono-acc text-xs text-[var(--ink-soft)] text-right shrink-0">
                      {d.start_date}<br />→ {d.end_date || "open"}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="md:col-span-5 grid gap-6">
            <Link to="/podcast" data-testid="home-card-podcast"
              className="block p-10 rounded-sm bg-[var(--forest)] text-white hover:-translate-y-1 transition-all duration-300">
              <Mic className="w-5 h-5 text-[var(--sand)] mb-4" strokeWidth={1.5} />
              <div className="overline text-[var(--sand)] mb-3">Daily podcast</div>
              <div className="font-serif-display text-2xl mb-3">
                {latestPodcast ? latestPodcast.title : "Voices of recovery"}
              </div>
              <p className="text-white/75 text-sm leading-relaxed">
                {latestPodcast?.description?.slice(0, 110) || "Daily interviews with the people we meet — the ones still fighting, and the ones who made it through."}
              </p>
            </Link>

            <Link to="/donate" data-testid="home-card-donate"
              className="block p-10 rounded-sm border border-[var(--line)] bg-[var(--bg)] hover:-translate-y-1 transition-all duration-300">
              <Heart className="w-5 h-5 text-[var(--terracotta)] mb-4" strokeWidth={1.5} />
              <div className="overline mb-3">Support the movement</div>
              <div className="font-serif-display text-2xl mb-2">Fuel another mile</div>
              <p className="text-[var(--ink-soft)] text-sm">Scan our QR or use the link. Every gift puts us in front of someone who needs to hear they're not alone.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* MEET UP CTA */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-20 text-center">
        <MessageCircle className="w-7 h-7 text-[var(--terracotta)] mx-auto mb-6" strokeWidth={1.25} />
        <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mb-6">
          Are we headed to your city?
        </h2>
        <p className="text-[var(--ink-soft)] text-lg max-w-2xl mx-auto leading-relaxed mb-8">
          If you're in recovery, in the middle of it, or you love someone who is — send us a private note.
          We answer every message, and we'll meet up whenever we can.
        </p>
        <Link to="/messages" data-testid="home-meetup-cta"
          className="inline-block bg-[var(--ink)] text-[var(--bg)] px-8 py-4 rounded-full text-sm hover:bg-[var(--forest)] transition-colors">
          Reach out
        </Link>
      </section>
    </div>
  );
}

function CouplePhotoSection({ about }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const rawPhoto = about?.couple_photo || "";
  const photo = rawPhoto ? (rawPhoto.startsWith("/api/") ? `${BACKEND}${rawPhoto}` : rawPhoto) : "";

  if (!photo) {
    return (
      <section data-testid="couple-photo-empty" className="max-w-4xl mx-auto px-6 md:px-10 pt-8 pb-4">
        <div className="w-full aspect-[3/2] rounded-2xl border border-dashed border-[var(--line)] bg-[var(--bg-alt)] flex items-center justify-center text-center px-6">
          <div>
            <div className="overline mb-3">Couple photo</div>
            <p className="text-sm text-[var(--ink-soft)] max-w-sm mx-auto">
              Upload a favorite photo of the two of you from{" "}
              <span className="font-mono-acc text-[var(--terracotta)]">/admin</span> → Our story → Couple photo.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="couple-photo-section" className="relative py-16 md:py-20 overflow-hidden">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="relative">
          {/* Warm ambient glow */}
          <div
            className="absolute -inset-10 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at 50% 40%, rgba(224,122,95,0.14) 0%, rgba(30,63,51,0.06) 40%, transparent 75%)",
              filter: "blur(40px)",
            }}
          />
          <figure className="relative rounded-2xl overflow-hidden ring-1 ring-[var(--line)]"
            style={{
              boxShadow:
                "0 30px 70px -20px rgba(0,0,0,0.30), 0 12px 30px -10px rgba(224,122,95,0.15)",
            }}>
            <img
              src={photo}
              alt="Rick and Stephanie McEntee"
              className="block w-full h-auto object-cover"
              loading="lazy"
            />
            <figcaption className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <div className="inline-flex items-center gap-3 backdrop-blur-md bg-black/35 text-white rounded-full pl-3 pr-5 py-2 ring-1 ring-white/15">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" />
                <span className="font-mono-acc text-[10px] tracking-[0.28em] uppercase text-white/80">Together in recovery</span>
                <span className="font-serif-display italic text-lg leading-none pl-1">Rick &amp; Stephanie</span>
              </div>
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}

function MeetSection({ about }) {
  const intro = (about?.intro || "").trim();
  const isPlaceholder = !intro || intro.startsWith("[");
  const paragraphs = isPlaceholder ? [] : intro.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

  return (
    <section data-testid="meet-section" className="max-w-3xl mx-auto px-6 md:px-10 pt-4 pb-20 md:pb-28">
      <div className="text-center mb-8">
        <div className="overline mb-4">Ask me how</div>
        <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-tight">
          Meet <em className="italic text-[var(--terracotta)]">Rick &amp; Stephanie</em><br />McEntee
        </h2>
      </div>

      {isPlaceholder ? (
        <div className="space-y-4 text-center">
          <p className="text-[var(--ink-soft)] text-lg leading-relaxed italic">
            Bio coming soon — Rick and Stephanie are writing their story right now.
          </p>
          <p className="text-sm text-[var(--ink-soft)] leading-relaxed">
            <span className="font-mono-acc text-[10px] tracking-widest text-[var(--terracotta)] mr-2">EDIT ME</span>
            Open <Link to="/admin" className="underline hover:text-[var(--terracotta)]">/admin</Link> → <em>Our story</em> tab and type your intro into the <strong>Intro</strong> field. Whatever you write there will appear here automatically.
          </p>
        </div>
      ) : (
        <div className="space-y-5 text-[var(--ink-soft)] text-lg leading-relaxed">
          {paragraphs.map((p, i) => (
            <p key={`${i}-${p.slice(0, 24)}`}>{p}</p>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3 mt-10 justify-center">
        <Link to="/about#our-story" data-testid="meet-full-story"
          className="inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--bg)] px-6 py-3 rounded-full text-sm hover:bg-[var(--forest)] transition-colors">
          Read our full story <ArrowUpRight className="w-4 h-4" />
        </Link>
        <Link to="/messages" data-testid="meet-message"
          className="inline-flex items-center gap-2 border border-[var(--line)] text-[var(--ink)] px-6 py-3 rounded-full text-sm hover:border-[var(--terracotta)] hover:text-[var(--terracotta)] transition-colors">
          Send us a note
        </Link>
      </div>

      <div className="mt-8 flex justify-center">
        <SocialLinks variant="footer" label="Follow along" />
      </div>
    </section>
  );
}
